'use client'

import { useEffect, useLayoutEffect, useState } from 'react'

/**
 * THE PORTAL MOTION SYSTEM — one stylesheet + two primitives, shared by the
 * landing page, the dashboard, and every chapter page.
 *
 * Atmosphere: a drifting gold aurora and a paper-grain overlay on the navy
 * field, and the compass rose in slow rotation. Choreography: sections rise
 * as they enter view (IntersectionObserver, once), the journey rail fills,
 * numbers count up (use-count-up.ts), the gold rule draws itself. Physics:
 * cards lift with a gold ring on hover; gold CTAs carry a sheen sweep.
 *
 * Everything is transform/opacity-only (GPU-cheap) and fully disabled under
 * prefers-reduced-motion — elements land in their final state.
 */

export function PortalMotionStyles() {
  return (
    <style>{`
      :root { --mp-ease: cubic-bezier(0.2, 0.7, 0.2, 1) }

      /* ── Atmosphere ─────────────────────────────────────── */
      @keyframes mpAurora {
        0%   { transform: translate3d(-6%, -4%, 0) scale(1) }
        50%  { transform: translate3d(5%, 3%, 0) scale(1.12) }
        100% { transform: translate3d(-6%, -4%, 0) scale(1) }
      }
      .mp-aurora {
        position: fixed; inset: -20%; z-index: 0; pointer-events: none;
        background:
          radial-gradient(42% 34% at 22% 18%, rgba(223,192,106,0.07), transparent 70%),
          radial-gradient(36% 30% at 78% 72%, rgba(21,42,66,0.55), transparent 72%),
          radial-gradient(30% 26% at 68% 12%, rgba(223,192,106,0.045), transparent 70%);
        animation: mpAurora 26s ease-in-out infinite;
        will-change: transform;
      }
      .mp-grain {
        position: fixed; inset: 0; z-index: 0; pointer-events: none; opacity: 0.05;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E");
      }
      @keyframes mpSpin { to { transform: rotate(360deg) } }
      .mp-compass { animation: mpSpin 140s linear infinite; will-change: transform }

      /* ── Choreography ───────────────────────────────────── */
      .mp-reveal { opacity: 0; transform: translateY(18px) }
      .mp-reveal.mp-in {
        opacity: 1; transform: none;
        transition: opacity 0.65s var(--mp-ease), transform 0.65s var(--mp-ease);
      }
      @keyframes mpRule { from { transform: scaleX(0) } to { transform: scaleX(1) } }
      .mp-rule { transform-origin: left; animation: mpRule 1.1s 0.15s var(--mp-ease) both }
      @keyframes mpRing { from { stroke-dashoffset: var(--mp-ring-c) } to { stroke-dashoffset: var(--mp-ring-t) } }
      @keyframes mpLineFill { from { transform: scaleX(0) } to { transform: scaleX(1) } }
      .mp-line-done { transform-origin: left; animation: mpLineFill 0.9s 0.3s var(--mp-ease) both }
      @keyframes mpNodePulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(223,192,106,0.45) }
        55%      { box-shadow: 0 0 0 9px rgba(223,192,106,0) }
      }
      .mp-node { animation: mpNodePulse 2.6s ease-out infinite }
      @keyframes mpDockIn { from { opacity: 0; transform: translateY(22px) } to { opacity: 1; transform: none } }
      .mp-dock { animation: mpDockIn 0.6s 0.9s var(--mp-ease) both }
      @keyframes mpBreathe {
        0%, 100% { border-color: rgba(223,192,106,0.35) }
        50%      { border-color: rgba(223,192,106,0.75) }
      }
      .mp-breathe { animation: mpBreathe 3.4s ease-in-out infinite }
      @keyframes mpTwinkle {
        0%, 100% { opacity: 1; transform: scale(1) }
        50%      { opacity: 0.72; transform: scale(0.88) }
      }
      .mp-star { transform-box: fill-box; transform-origin: center; animation: mpTwinkle 3.8s ease-in-out infinite }

      /* ── Physics ────────────────────────────────────────── */
      .mp-col > * {
        transition: transform 0.22s var(--mp-ease), box-shadow 0.22s var(--mp-ease);
      }
      .mp-col > *:hover {
        transform: translateY(-2px);
        box-shadow: 0 18px 44px -22px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(223,192,106,0.22);
      }
      .mp-btn { position: relative; overflow: hidden }
      .mp-btn::after {
        content: ''; position: absolute; inset: 0; pointer-events: none;
        background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.5) 46%, transparent 62%);
        transform: translateX(-160%);
      }
      .mp-btn:hover::after { transform: translateX(160%); transition: transform 0.7s ease }

      /* ── Chapter cards (landing index) ──────────────────── */
      .mp-chapter { transition: transform 0.24s var(--mp-ease), box-shadow 0.24s var(--mp-ease), background 0.24s var(--mp-ease) }
      .mp-chapter:hover {
        transform: translateY(-3px);
        background: rgba(255,255,255,0.06) !important;
        box-shadow: 0 22px 54px -24px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(223,192,106,0.3);
      }
      .mp-chapter .mp-chapter-arrow { transition: transform 0.24s var(--mp-ease) }
      .mp-chapter:hover .mp-chapter-arrow { transform: translateX(5px) }

      /* ── Scroll progress ────────────────────────────────── */
      .mp-progress {
        position: fixed; top: 0; left: 0; right: 0; height: 2px; z-index: 60;
        background: linear-gradient(90deg, #C7A84F, #DFC06A);
        transform-origin: left; transform: scaleX(0); pointer-events: none;
      }

      /* ── Reduced motion: land everything in final state ─── */
      @media (prefers-reduced-motion: reduce) {
        .mp-aurora, .mp-compass, .mp-node, .mp-breathe, .mp-star { animation: none !important }
        .mp-rule, .mp-line-done, .mp-dock { animation: none !important; transform: none }
        .mp-reveal { opacity: 1 !important; transform: none !important; transition: none !important }
        .mp-col > *, .mp-chapter, .mp-btn::after { transition: none !important }
        .mp-ring-arc, circle { animation: none !important }
      }
    `}</style>
  )
}

/** Fixed 2px gold bar tracking scroll depth — the letterhead's rule, live. */
export function ScrollProgressBar() {
  const [p, setP] = useState(0)
  useEffect(() => {
    let raf = 0
    const on = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const h = document.documentElement
        const max = h.scrollHeight - h.clientHeight
        setP(max > 0 ? h.scrollTop / max : 0)
      })
    }
    on()
    window.addEventListener('scroll', on, { passive: true })
    window.addEventListener('resize', on)
    return () => {
      window.removeEventListener('scroll', on)
      window.removeEventListener('resize', on)
      cancelAnimationFrame(raf)
    }
  }, [])
  return <div className="mp-progress" style={{ transform: `scaleX(${p})` }} aria-hidden="true" />
}

/**
 * Rise-on-scroll for a column's direct children. Call with the column id;
 * children get .mp-reveal pre-paint and .mp-in (staggered per intersection
 * batch) as they enter view. Deps: pass values that change when the column's
 * children change so late-rendered sections get observed too.
 */
export function useRevealColumn(columnId: string, deps: unknown[] = []) {
  useLayoutEffect(() => {
    const col = document.getElementById(columnId)
    if (!col || typeof IntersectionObserver === 'undefined') return
    const kids = Array.from(col.children) as HTMLElement[]
    kids.forEach((el) => el.classList.add('mp-reveal'))
    let batch = 0
    let batchAt = 0
    const io = new IntersectionObserver(
      (entries) => {
        const now = performance.now()
        if (now - batchAt > 450) batch = 0
        batchAt = now
        for (const e of entries) {
          if (!e.isIntersecting) continue
          const el = e.target as HTMLElement
          el.style.transitionDelay = `${Math.min(batch * 70, 420)}ms`
          el.classList.add('mp-in')
          batch++
          io.unobserve(el)
        }
      },
      { threshold: 0.06, rootMargin: '0px 0px -6% 0px' },
    )
    kids.forEach((el) => io.observe(el))
    return () => io.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
